import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Reveal } from "./Motion";
import { PORTFOLIO } from "../data";

const Card = ({ item, onOpen }) => {
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
      className={`group relative overflow-hidden bg-[#0c0c0c] min-h-[300px] text-left ${item.span}`}
      data-testid={`portfolio-item-${item.id}`}
    >
      <img
        src={item.url}
        alt={item.title}
        className="absolute inset-0 w-full h-full object-cover grayscale opacity-50 scale-100 transition-all duration-700 ease-out group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-80" />
      <div className="absolute inset-0 border border-white/0 group-hover:border-[#5cc6ff]/40 transition-colors duration-500" />
      <span className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-[#5cc6ff]">
        <Maximize2 size={20} strokeWidth={1.5} />
      </span>
      <span className="absolute bottom-4 left-4 font-outfit text-[10px] uppercase tracking-[0.3em] text-white/0 group-hover:text-white/70 transition-colors duration-300">
        {item.tag}
      </span>
    </motion.button>
  );
};

const Lightbox = ({ index, onClose, onPrev, onNext }) => {
  const item = PORTFOLIO[index];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[200] bg-[#050505]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        className="absolute top-6 right-6 text-white/70 hover:text-[#5cc6ff] transition-colors z-10"
        aria-label="Close"
      >
        <X size={32} strokeWidth={1.5} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        data-testid="lightbox-prev"
        className="absolute left-4 md:left-8 text-white/60 hover:text-[#5cc6ff] transition-colors z-10"
        aria-label="Previous"
      >
        <ChevronLeft size={44} strokeWidth={1.25} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        data-testid="lightbox-next"
        className="absolute right-4 md:right-8 text-white/60 hover:text-[#5cc6ff] transition-colors z-10"
        aria-label="Next"
      >
        <ChevronRight size={44} strokeWidth={1.25} />
      </button>

      <motion.div
        key={item.id}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
        className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={item.url}
          alt={item.title}
          className="max-w-full max-h-[78vh] object-contain border border-white/10"
          data-testid="lightbox-image"
        />
        <div className="flex items-center gap-4 mt-5">
          <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#5cc6ff]">
            {item.tag}
          </span>
          <span className="w-8 h-px bg-white/20" />
          <span className="font-anton uppercase text-xl text-white">{item.title}</span>
          <span className="font-outfit text-xs text-white/40 ml-2">
            {index + 1} / {PORTFOLIO.length}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Portfolio = () => {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(
    () => setActive((i) => (i === null ? i : (i - 1 + PORTFOLIO.length) % PORTFOLIO.length)),
    []
  );
  const next = useCallback(
    () => setActive((i) => (i === null ? i : (i + 1) % PORTFOLIO.length)),
    []
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, prev, next]);

  return (
    <section id="portfolio" className="relative py-20 md:py-28 max-w-[1600px] mx-auto px-6 md:px-12" data-testid="portfolio-section">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="w-12 h-px bg-[#5cc6ff]" />
              <span className="font-outfit text-xs uppercase tracking-[0.35em] text-white/50">
                My Gallery | Selected Work
              </span>
            </div>
            <h2 className="font-anton uppercase text-5xl md:text-8xl leading-[0.85] text-white">
              The <span className="text-[#5cc6ff]">Archive</span>
            </h2>
          </div>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 auto-rows-[minmax(280px,auto)]">
        {PORTFOLIO.map((item, i) => (
          <Card key={item.id} item={item} onOpen={() => setActive(i)} />
        ))}
      </div>

      <AnimatePresence>
        {active !== null && (
          <Lightbox index={active} onClose={close} onPrev={prev} onNext={next} />
        )}
      </AnimatePresence>
    </section>
  );
};
